import type { ComponentType } from 'react';
import React, { useMemo } from 'react';
import type { JsonLd } from 'json-ld-types';

import type { CardState } from '@atlaskit/linking-common';

import { expVal } from '@confluence/feature-experiments';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers';
import { useSessionData } from '@confluence/session-data';
import { isPageSSRd, useStoreOptions as useStoreOptionsOld } from '@confluence/content-smartlinks';
import { fg } from '@confluence/feature-gating';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';

const useInitialState = () => {
	return useMemo(() => {
		const ssrProvidersData: { editorCardProvider?: Record<string, JsonLd.Response> } =
			window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] || {};
		const ssrData = ssrProvidersData.editorCardProvider;
		if (!ssrData) {
			return undefined;
		}

		return Object.entries(ssrData).reduce<Record<string, CardState>>((acc, [url, data]) => {
			acc[url] = {
				status: 'resolved',
				details: data,
				metadataStatus: 'resolved',
			};

			return acc;
		}, {});
	}, []);
};

const useStoreOptions = () => {
	const isPageSSR = isPageSSRd();
	const initialState = useInitialState();
	const oldInitialState = window['__SSR_SMARTLINKS__'];
	const oldStoreOptions = useStoreOptionsOld(oldInitialState, isPageSSR);

	return useMemo(() => {
		if (initialState && expVal('platform_editor_smart_card_otp', 'isEnabled', false)) {
			return {
				initialState,
			};
		}

		// Should be removed when the platform_editor_smart_card_otp experiment is rolled out
		if (fg('platform_ssr_smartlinks_editor')) {
			return oldStoreOptions.storeOptions;
		}

		return undefined;
	}, [oldStoreOptions, initialState]);
};

export function withSmartCardProviderContext<T>(WrappedComponent: ComponentType<T>) {
	return (props: any) => {
		const { isAdminHubAIEnabled } = useSessionData();

		const storeOptions = useStoreOptions();

		const content = <WrappedComponent {...props} />;

		return (
			<>
				{fg('confluence_smart_card_provider_preview_panel') ? (
					content
				) : (
					<ConfluenceSmartCardProvider
						{...getSmartCardRenderers()}
						storeOptions={storeOptions}
						isAdminHubAIEnabled={isAdminHubAIEnabled}
					>
						{content}
					</ConfluenceSmartCardProvider>
				)}
			</>
		);
	};
}
